import { useEffect, useMemo, useState } from "react";
import { cn } from "@/lib/utils";

export interface MCPServer {
  key: string;
  name: string;
  type: "streamable-http";
  url: string;
  description: string;
  defaultEnabled: boolean;
  category: string;
  /** Portal this server belongs to — drives the themed presets. */
  theme?: "genomics" | "pharma" | "clinical";
}

export interface MCPStatus {
  ok: boolean;
  status?: number;
  latencyMs: number;
  error?: string;
  checkedAt: number;
}

interface Props {
  selectedKeys: Set<string>;
  onChange: (keys: Set<string>) => void;
  servers: MCPServer[];
}

export function useMCPServerCatalog() {
  const [servers, setServers] = useState<MCPServer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch("/api/mcp-servers")
      .then((r) => r.json() as Promise<{ servers?: MCPServer[] }>)
      .then((j) => {
        if (cancelled) return;
        setServers(Array.isArray(j.servers) ? j.servers : []);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { servers, loading, error };
}

const STATUS_POLL_INTERVAL_MS = 30_000;

export function useMCPServerStatus() {
  const [statuses, setStatuses] = useState<Record<string, MCPStatus>>({});
  const [lastChecked, setLastChecked] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const poll = async () => {
      try {
        const r = await fetch("/api/mcp-status");
        const j = (await r.json()) as { servers?: Record<string, MCPStatus> };
        if (cancelled) return;
        setStatuses(j.servers ?? {});
        setLastChecked(Date.now());
      } catch (err) {
        console.warn("mcp-status poll failed:", err);
      }
      if (!cancelled) {
        timer = setTimeout(poll, STATUS_POLL_INTERVAL_MS);
      }
    };

    void poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  return { statuses, lastChecked };
}

function StatusDot({
  status,
  size = "sm",
}: {
  status: MCPStatus | undefined;
  size?: "sm" | "md";
}) {
  const dim = size === "sm" ? "h-2 w-2" : "h-3 w-3";
  if (!status) {
    return (
      <span
        className={cn(
          "inline-block rounded-full bg-muted-foreground/40",
          dim,
        )}
        title="Status unknown"
        data-status="unknown"
      />
    );
  }
  const color = status.ok ? "bg-emerald-500" : "bg-red-500";
  const tooltip = status.ok
    ? `Healthy · ${status.latencyMs}ms`
    : `Down: ${status.error || "unknown"}`;
  return (
    <span
      className={cn(
        "inline-block rounded-full",
        dim,
        color,
        status.ok && "status-pulse-healthy",
      )}
      title={tooltip}
      data-status={status.ok ? "healthy" : "down"}
      data-testid={status.ok ? "status-dot-healthy" : "status-dot-down"}
    />
  );
}

export function MCPServerPicker({ selectedKeys, onChange, servers }: Props) {
  const [open, setOpen] = useState(false);
  const { statuses } = useMCPServerStatus();

  const grouped = useMemo(() => {
    const out: Record<string, MCPServer[]> = {};
    for (const s of servers) {
      const cat = s.category || "other";
      if (!out[cat]) out[cat] = [];
      out[cat].push(s);
    }
    return out;
  }, [servers]);

  const summary = useMemo(() => {
    let healthy = 0;
    let down = 0;
    for (const s of servers) {
      const st = statuses[s.key];
      if (st?.ok) healthy++;
      else if (st && !st.ok) down++;
    }
    return { healthy, down, total: servers.length };
  }, [servers, statuses]);

  const overallDot =
    summary.down > 0 ? "down" : summary.healthy > 0 ? "healthy" : "unknown";

  const toggle = (key: string) => {
    const next = new Set(selectedKeys);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(next);
  };

  const enableAll = () => onChange(new Set(servers.map((s) => s.key)));
  const enableHealthy = () =>
    onChange(new Set(servers.filter((s) => statuses[s.key]?.ok).map((s) => s.key)));
  const disableAll = () => onChange(new Set());

  return (
    <div className="relative" data-testid="mcp-picker">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="mcp-picker-toggle"
        className={cn(
          "flex items-center gap-2 rounded-md border border-input bg-background px-3 py-1.5 text-sm hover:bg-accent",
          selectedKeys.size > 0 && "ring-2 ring-ring",
        )}
        title={`${summary.healthy} healthy · ${summary.down} down · ${selectedKeys.size} enabled`}
      >
        <span
          className={cn(
            "inline-block h-2 w-2 rounded-full",
            overallDot === "healthy" && "bg-emerald-500 status-pulse-healthy",
            overallDot === "down" && "bg-red-500",
            overallDot === "unknown" && "bg-muted-foreground/40",
          )}
          data-testid="mcp-picker-summary-dot"
          data-status={overallDot}
        />
        <span>
          Tools: {selectedKeys.size}/{servers.length}
        </span>
      </button>
      {open && (
        <div
          data-testid="mcp-picker-panel"
          className="absolute right-0 top-full z-50 mt-1 max-h-[60vh] w-96 overflow-y-auto rounded-md border border-border bg-popover p-2 shadow-lg"
          onMouseLeave={() => setOpen(false)}
        >
          <div className="flex items-center justify-between border-b border-border pb-2 mb-2 gap-2">
            <span className="flex items-center gap-2 text-xs">
              <span className="font-semibold uppercase tracking-wider text-muted-foreground">
                MCP servers
              </span>
              <span className="text-muted-foreground">
                {summary.healthy}↑ {summary.down}↓
              </span>
            </span>
            <div className="flex gap-1">
              <button
                type="button"
                onClick={enableHealthy}
                className="rounded px-1.5 py-0.5 text-xs hover:bg-accent"
                title="Enable only servers that responded healthy"
              >
                Healthy
              </button>
              <button
                type="button"
                onClick={enableAll}
                className="rounded px-1.5 py-0.5 text-xs hover:bg-accent"
              >
                All
              </button>
              <button
                type="button"
                onClick={disableAll}
                className="rounded px-1.5 py-0.5 text-xs hover:bg-accent"
              >
                None
              </button>
            </div>
          </div>
          {Object.entries(grouped).map(([cat, list]) => (
            <div key={cat} className="mb-2">
              <div className="px-1 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {cat}
              </div>
              <ul className="space-y-0.5">
                {list.map((s) => {
                  const st = statuses[s.key];
                  return (
                    <li key={s.key}>
                      <label
                        className="flex cursor-pointer items-start gap-2 rounded px-1 py-1 text-xs hover:bg-accent"
                        title={st ? (st.ok ? `Healthy · ${st.latencyMs}ms` : `Down: ${st.error || "unknown"}`) : s.description}
                        data-testid={`mcp-row-${s.key}`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedKeys.has(s.key)}
                          onChange={() => toggle(s.key)}
                          data-testid={`mcp-toggle-${s.key}`}
                          className="mt-0.5"
                        />
                        <StatusDot status={st} />
                        <span className="flex-1">
                          <span className="font-medium">{s.name}</span>
                          <span className="block text-[10px] leading-tight text-muted-foreground">
                            {st && !st.ok ? (
                              <span className="text-destructive">
                                {st.error || `HTTP ${st.status}`}
                              </span>
                            ) : (
                              s.description
                            )}
                          </span>
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
